import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';

import { environment } from '@env/environment';

import {
  Project,
  ProjectInfo,
  ProjectInterview,
  ProjectInterviewLog,
  ProjectInterviewSurveyQuestionResponse,
  ProjectListItem,
  QBATProjectResponse,
} from '@app/models';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  refreshProjects: Subject<void> = new Subject();

  constructor(private http: HttpClient) {}

  getProjects(appId: number): Observable<ProjectListItem[]> {
    return this.http.get<ProjectListItem[]>(`${environment.apiUrl}/api/project/getProjects?applicationId=${appId}`);
  }

  getProjectsInfo(appId: number): Observable<ProjectInfo[]> {
    return this.http.get<ProjectInfo[]>(`${environment.apiUrl}/api/project/getProjectsInfo?appId=${appId}`);
  }

  getProject(projectId: number): Observable<Project> {
    return this.http.get<Project>(`${environment.apiUrl}/api/project/getProject?projectId=${projectId}`);
  }

  getProjectAsQBAT(projectId: number): Observable<QBATProjectResponse> {
    return this.http.get<QBATProjectResponse>(
      `${environment.apiUrl}/api/project/getProjectAsQbat?projectId=${projectId}`
    );
  }

  saveProject(project: any): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/api/project/saveProject`, project)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  deleteProject(projectId: number): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/api/project/deleteProject`, {
        projectId: projectId,
      })
      .pipe(tap(() => this.refreshProjects.next()));
  }

  setCopiedProjectAsNewProject(projectId: number): Observable<boolean> {
    return this.http
      .post<any>(`${environment.apiUrl}/api/project/setCopiedProjectAsNewProject`, {
        projectId: projectId,
      })
      .pipe(tap(() => this.refreshProjects.next()));
  }

  getProjectNotes(projectId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/project/getProjectNotes?projectId=${projectId}`);
  }

  saveProjectNotes(projectId: number, notes: string): Observable<any> {
    const payload = {
      projectId,
      notes,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/saveProjectNotes`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  createProjectInterview(
    projectId: number,
    fullName: string,
    email: string,
    phone: string,
    projectName: string,
    position: string,
    sendEmail: boolean = false
  ): Observable<any> {
    const payload = {
      projectId,
      fullName,
      email,
      phone,
      projectName,
      position,
      sendEmail,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/createProjectInterview`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  editProjectInterviewContact(
    token: string,
    fullname: string,
    email: string,
    phone: string,
    projectName: string,
    position: string,
    sendEmail: boolean = false
  ): Observable<any> {
    const payload = {
      token,
      fullname,
      email,
      phone,
      projectName,
      position,
      sendEmail,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/editProjectInterviewContact`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  getProjectInterviews(projectId: number): Observable<ProjectInterview[]> {
    return this.http.get<ProjectInterview[]>(
      `${environment.apiUrl}/api/project/getProjectInterviews?projectId=${projectId}`
    );
  }

  getProjectInterview(token: string): Observable<ProjectInterview> {
    return this.http.get<ProjectInterview>(`${environment.apiUrl}/api/project/getProjectInterview?token=${token}`);
  }

  createProjectInterviewLog(token: string, message: string): Observable<any> {
    const payload = {
      token,
      message,
    };

    return this.http.post<any>(`${environment.apiUrl}/api/project/createProjectInterviewLog`, payload);
  }

  getProjectInterviewLogs(token: string): Observable<ProjectInterviewLog[]> {
    return this.http.get<any>(`${environment.apiUrl}/api/project/getProjectInterviewLogs?token=${token}`);
  }

  getProjectInterviewSurveyQuestions(surveyId: number): Observable<ProjectInterviewSurveyQuestionResponse[]> {
    return this.http.get<any>(
      `${environment.apiUrl}/api/project/getProjectInterviewSurveyQuestions?surveyId=${surveyId}`
    );
  }

  submitProjectInterviewSurvey(surveyData: any): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/api/project/submitProjectInterviewSurvey`, surveyData)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  sendProjectInterviewSurvey(token: string): Observable<any> {
    const payload = {
      token,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/sendProjectInterviewSurvey`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  forwardProjectInterviewSurvey(
    token: string,
    fullname: string,
    email: string,
    phone: string,
    projectName: string,
    projectId: number,
    position: string,
    sendEmail: boolean = false
  ): Observable<any> {
    const payload = {
      token,
      fullname,
      email,
      phone,
      projectName,
      projectId,
      position,
      sendEmail,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/forwardProjectInterviewSurvey`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }

  cancelProjectInterviewSurvey(token: string): Observable<any> {
    const payload = {
      token,
    };

    return this.http
      .post<any>(`${environment.apiUrl}/api/project/cancelProjectInterviewSurvey`, payload)
      .pipe(tap(() => this.refreshProjects.next()));
  }
}
